import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories, events } from '@/mocks/events';
import EventCard from '@/components/feature/EventCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';
import FilterBar from './components/FilterBar';

export default function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const keyword = searchParams.get('q') || '';
  const [sort, setSort] = useState<'hot' | 'latest'>('hot');
  const [inputValue, setInputValue] = useState(keyword);

  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (inputValue.trim()) {
        next.set('q', inputValue.trim());
      } else {
        next.delete('q');
      }
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    }, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const activeCategory = categories.find((cat) => cat.id === activeCat);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = events.filter((item) => {
      const matchCat = activeCat === 'all' || item.categoryId === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = [item.title, item.summary, item.campus, ...item.tags].join(' ').toLowerCase();
      return haystack.includes(q);
    });
    return list.sort((a, b) => (sort === 'hot' ? b.heat - a.heat : a.publishedAt < b.publishedAt ? 1 : -1));
  }, [activeCat, keyword, sort]);

  const handleCatChange = (catId: string) => {
    const next = new URLSearchParams(searchParams);
    if (catId === 'all') {
      next.delete('cat');
    } else {
      next.set('cat', catId);
    }
    setSearchParams(next);
  };

  const pageTitle = activeCategory ? `${activeCategory.name}事件合集` : '事件合集';

  usePageMeta({
    title: `${pageTitle}｜高校吃瓜事件与热点合集 - 高校吃瓜社`,
    description: `浏览高校吃瓜社的事件合集，覆盖${
      activeCategory ? activeCategory.name : '校园生活、学业考场、社团活动、宿舍日常等'
    }方向的高校吃瓜事件与热点，支持按分类筛选与关键词搜索，围观校园热门现场更高效。`,
    keywords: `高校吃瓜,高校吃瓜事件,${activeCategory ? activeCategory.name : '高校吃瓜热点'},高校吃瓜爆料,高校吃瓜新闻`,
    canonicalPath: activeCategory ? `/events?cat=${activeCategory.id}` : '/events',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: pageTitle,
      inLanguage: 'zh-CN',
      description: '高校吃瓜社校园事件合集，按话题分类整理校园热门事件。',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: pageTitle, item: '/events' },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 10).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `/events/${item.id}`,
        })),
      },
    },
  });

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-12 lg:px-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '事件合集', to: activeCategory ? '/events' : undefined },
              ...(activeCategory ? [{ label: `${activeCategory.name}` }] : []),
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {activeCategory ? `${activeCategory.name} · 校园事件合集` : '校园事件合集'}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {activeCategory
              ? `${activeCategory.desc}。以下是该方向下最新的校园热门事件，点击任意一条即可查看完整时间线与相关推荐。`
              : '这里汇总了高校吃瓜社的全部校园事件，涵盖校园生活、学业考场、社团活动、宿舍日常、校园美食、情感小站与实习求职等方向。用分类筛选或关键词搜索，快速找到你想围观的那件事。'}
          </p>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <FilterBar
            activeCat={activeCat}
            keyword={inputValue}
            sort={sort}
            onCatChange={handleCatChange}
            onKeywordChange={setInputValue}
            onSortChange={setSort}
            total={filtered.length}
          />

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-line text-2xl leading-none" aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到对应的事件</h2>
              <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者看看其他分类。</p>
              <Link
                to="/events"
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部事件
                <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}